import { useState, useEffect, useRef } from "react";
import { User, Camera, Lock, Eye, EyeOff, Check, X, Upload } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedName, setEditedName] = useState("");
  
  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Image upload state
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
        setEditedName(data.name || "");
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!editedName.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: editedName }),
      });
      
      if (response.ok) {
        const updatedProfile = await response.json();
        setProfile(updatedProfile);
        setEditMode(false);
        toast.success("Profile updated successfully");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update profile");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadImage = async () => {
    if (!imagePreview) return;
    
    setUploadingImage(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile/upload-picture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ picture: imagePreview }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile({ ...profile, picture: data.picture });
        setImagePreview(null);
        toast.success("Profile picture updated");
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const cancelImageUpload = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.new_password) {
      errors.new_password = "New password is required";
    } else if (passwordData.new_password.length < 6) {
      errors.new_password = "Password must be at least 6 characters";
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = "Passwords do not match";
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePassword()) return;
    
    setChangingPassword(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ current_password: passwordData.old_password, new_password: passwordData.new_password }),
      });
      
      if (response.ok) {
        toast.success("Password changed successfully");
        setShowPasswordForm(false);
        setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
        setPasswordErrors({});
      } else {
        const error = await response.json();
        if (error.detail === "Current password is incorrect") {
          setPasswordErrors({ old_password: "Current password is incorrect" });
        } else {
          toast.error(error.detail || "Failed to change password");
        }
      }
    } catch (error) {
      toast.error("Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-700 border-red-200";
      case "manager":
        return "bg-blue-100 text-blue-700 border-blue-200";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded-lg w-48"></div>
        <div className="h-64 bg-slate-200 rounded-2xl"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-4xl font-heading font-bold text-slate-900 dark:text-white tracking-tight">
          My Profile
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm sm:text-base">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="lg:col-span-1 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
          <CardContent className="p-6">
            {/* Profile Picture */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  ) : profile?.picture ? (
                    <img
                      src={profile.picture}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-16 h-16 text-white" />
                  )}
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-10 h-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                  data-testid="upload-picture-btn"
                >
                  <Camera className="w-5 h-5" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  data-testid="profile-picture-input"
                />
              </div>
              
              {/* Image Preview Actions */}
              {imagePreview && (
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={handleUploadImage}
                    disabled={uploadingImage}
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                    data-testid="save-picture-btn"
                  >
                    {uploadingImage ? (
                      "Uploading..."
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={cancelImageUpload}
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    data-testid="cancel-picture-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              )}
              
              {/* Name */}
              <div className="mt-4 text-center">
                {editMode ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="text-center font-semibold"
                      data-testid="edit-name-input"
                    />
                    <Button
                      onClick={handleSaveProfile}
                      disabled={saving}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 rounded-full"
                      data-testid="save-name-btn"
                    >
                      <Check className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => {
                        setEditMode(false);
                        setEditedName(profile?.name || "");
                      }}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <h2
                    className="text-xl font-heading font-bold text-slate-900 dark:text-white cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => setEditMode(true)}
                    data-testid="profile-name"
                  >
                    {profile?.name}
                  </h2>
                )}
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                  {profile?.email}
                </p>
                <Badge className={`mt-2 ${getRoleBadgeColor(profile?.role)} border`}>
                  {profile?.role?.charAt(0).toUpperCase() + profile?.role?.slice(1)}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Details & Password */}
        <div className="lg:col-span-2 space-y-6">
          {/* Account Information */}
          <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <User className="w-5 h-5 text-indigo-600" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-sm">Full Name</Label>
                  <p className="text-slate-900 dark:text-white font-medium">{profile?.name}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-sm">Email Address</Label>
                  <p className="text-slate-900 dark:text-white font-medium">{profile?.email}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-sm">Role</Label>
                  <p className="text-slate-900 dark:text-white font-medium capitalize">{profile?.role}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-sm">User ID</Label>
                  <p className="text-slate-500 dark:text-slate-400 font-mono text-sm">{profile?.user_id}</p>
                </div>
              </div>
              {!editMode && (
                <Button
                  onClick={() => setEditMode(true)}
                  variant="outline"
                  className="rounded-full"
                  data-testid="edit-profile-btn"
                >
                  Edit Profile
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-heading font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-indigo-600" />
                Password & Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!showPasswordForm ? (
                <div>
                  <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
                    Keep your account secure by using a strong password.
                  </p>
                  <Button
                    onClick={() => setShowPasswordForm(true)}
                    variant="outline"
                    className="rounded-full"
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Current Password */}
                  <div>
                    <Label htmlFor="old_password">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="old_password"
                        type={showPasswords.old ? "text" : "password"}
                        value={passwordData.old_password}
                        onChange={(e) => setPasswordData({ ...passwordData, old_password: e.target.value })}
                        className={`pr-10 ${passwordErrors.old_password ? "border-red-500" : ""}`}
                        placeholder="Enter current password"
                        data-testid="old-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, old: !showPasswords.old })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.old ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.old_password && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.old_password}</p>
                    )}
                    <p className="text-slate-400 text-xs mt-1">Leave empty if you signed up with Google</p>
                  </div>

                  {/* New Password */}
                  <div>
                    <Label htmlFor="new_password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="new_password"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        className={`pr-10 ${passwordErrors.new_password ? "border-red-500" : ""}`}
                        placeholder="Enter new password"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.new_password && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.new_password}</p>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm_password"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                        className={`pr-10 ${passwordErrors.confirm_password ? "border-red-500" : ""}`}
                        placeholder="Confirm new password"
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordErrors.confirm_password && (
                      <p className="text-red-500 text-xs mt-1">{passwordErrors.confirm_password}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full"
                      data-testid="submit-password-btn"
                    >
                      {changingPassword ? "Changing..." : "Update Password"}
                    </Button>
                    <Button
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({ old_password: "", new_password: "", confirm_password: "" });
                        setPasswordErrors({});
                      }}
                      variant="outline"
                      className="rounded-full"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
